import {Form as InertiaForm, Link} from '@inertiajs/react'
import { type ComponentProps, useEffect, useState } from 'react'

import { TaxPayerType } from './types'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {Card} from "@/components/ui/card.tsx";
import {Textarea} from "@/components/ui/textarea.tsx";
import {Button} from "@/components/ui/button.tsx";

type FormProps = Omit<ComponentProps<typeof InertiaForm>, 'children'> & {
  tax_payer: TaxPayerType
  submitText: string
  categories?: { id: string; name: string }[]
  areas?: { id: string; name: string }[]
}

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
] as const

export default function Form({
  tax_payer,
  submitText,
  categories = [],
  areas = [],
  ...formProps
}: FormProps) {
  const initialStatus = tax_payer.status || 'active'
  const initialCategoryId = tax_payer.category_id || categories[0]?.id || ''
  const initialAreaId = tax_payer.area_id || areas[0]?.id || ''
  const [status, setStatus] = useState(initialStatus)
  const [categoryId, setCategoryId] = useState(initialCategoryId)
  const [areaId, setAreaId] = useState(initialAreaId)

  useEffect(() => {
    setStatus(initialStatus)
    setCategoryId(initialCategoryId)
    setAreaId(initialAreaId)
  }, [initialStatus, initialCategoryId, initialAreaId])

  return (
    <InertiaForm
      transform={data => ({ tax_payer: data })}
      className="contents"
      {...formProps}
    >
      {({ errors, processing }) => (
        <>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card className="border-border bg-card p-6">
            <h3 className="mb-6 text-lg font-semibold text-foreground">Personal Information</h3>
            <div className="space-y-4">

              <div className="grid gap-2">
                <Label htmlFor="name">Name</Label>
                <Input
                    type="text"
                    name="name"
                    id="name"
                    defaultValue={tax_payer.name}
                />
                {errors.name && (
                    <div className="text-red-500 px-3 py-2 font-medium">
                      {errors.name.join(', ')}
                    </div>
                )}
              </div>
              <div className="grip gap-2">
                <Label htmlFor="fullName">Fullname</Label>
                <Input
                    type="text"
                    name="fullName"
                    id="fullName"
                    defaultValue={tax_payer.fullName}
                />
                {errors.fullName && (
                    <div className="text-red-500 px-3 py-2 font-medium">
                      {errors.fullName.join(', ')}
                    </div>
                )}
              </div>
              <div className="grip gap-2">
                <Label htmlFor="nif">NIF</Label>
                <Input
                    type="text"
                    name="nif"
                    id="nif"
                    defaultValue={tax_payer.nif}
                />
                {errors.nif && (
                    <div className="text-red-500 px-3 py-2 font-medium">
                      {errors.nif.join(', ')}
                    </div>
                )}
              </div>
              <div className="grip gap-2">
                <Label htmlFor="rccm">RCCM</Label>
                <Input
                    type="text"
                    name="rccm"
                    id="rccm"
                    defaultValue={tax_payer.rccm}
                />
                {errors.rccm && (
                    <div className="text-red-500 px-3 py-2 font-medium">
                      {errors.rccm.join(', ')}
                    </div>
                )}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                    type="text"
                    name="email"
                    id="email"
                    placeholder="mail@business.com"
                    defaultValue={tax_payer.email}
                />
                {errors.email && (
                    <div className="text-red-500 px-3 py-2 font-medium">
                      {errors.email.join(', ')}
                    </div>
                )}

              </div>

              <div className="grid gap-2">
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                    type="text"
                    name="phone"
                    id="phone"
                    placeholder={"+228 90 00 00 00"}
                    defaultValue={tax_payer.phone}
                />
                {errors.phone && (
                    <div className="text-red-500 px-3 py-2 font-medium">
                      {errors.phone.join(', ')}
                    </div>
                )}
              </div>

            </div>
          </Card>

          <Card className="border-border bg-card p-6">
            <h3 className="mb-6 text-lg font-semibold text-foreground">Property Information</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="category_id">Category</Label>
                  <input type="hidden" name="category_id" value={categoryId} />
                  <Select value={categoryId} onValueChange={setCategoryId}>
                    <SelectTrigger id="category_id" className={"w-full"}>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.category_id && (
                      <div className="text-red-500 px-3 py-2 font-medium">
                        {errors.category_id.join(', ')}
                      </div>
                  )}
                </div>
                <div className="grid gap-2">

                  <Label htmlFor="area_id">Area</Label>
                  <input type="hidden" name="area_id" value={areaId} />
                  <Select value={areaId} onValueChange={setAreaId}>
                    <SelectTrigger id="area_id" className={"w-full"}>
                      <SelectValue placeholder="Select area" />
                    </SelectTrigger>
                    <SelectContent>
                      {areas.map((area) => (
                          <SelectItem key={area.id} value={area.id}>
                            {area.name}
                          </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.area_id && (
                      <div className="text-red-500 px-3 py-2 font-medium">
                        {errors.area_id.join(', ')}
                      </div>
                  )}
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="address">Address</Label>
                <Input
                    type="text"
                    name="address"
                    id="address"
                    placeholder={"adresse de la structure"}
                    defaultValue={tax_payer.address}
                />
                {errors.address && (
                    <div className="text-red-500 px-3 py-2 font-medium">
                      {errors.address.join(', ')}
                    </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="latitude">Latitude</Label>
                  <Input
                      type="number"
                      name="latitude"
                      id="latitude"
                      defaultValue={tax_payer.latitude}
                  />
                  {errors.latitude && (
                      <div className="text-red-500 px-3 py-2 font-medium">
                        {errors.latitude.join(', ')}
                      </div>
                  )}
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="longitude">Longitude</Label>
                  <Input
                      type="number"
                      name="longitude"
                      id="longitude"
                      defaultValue={tax_payer.longitude}
                  />
                  {errors.longitude && (
                      <div className="text-red-500 px-3 py-2 font-medium">
                        {errors.longitude.join(', ')}
                      </div>
                  )}
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="status">Status</Label>
                <input type="hidden" name="status" value={status} />
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger id="status" className={"w-full"}>
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.status && (
                    <div className="text-red-500 px-3 py-2 font-medium">
                      {errors.status.join(', ')}
                    </div>
                )}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea id="notes" placeholder="Additional notes about the taxpayer..." rows={4} />
              </div>
            </div>
          </Card>
        </div>


          <div className="inline">
            <Button
              type="submit"
              disabled={processing}
              className="cursor-pointer"
            >
              {submitText}
            </Button>
            <Link
                href="/tax_payers"
                className="ml-2 rounded-lg py-1.5 px-5 bg-secondary inline-block"
            >
              Back to tax payers
            </Link>
          </div>
        </>
      )}
    </InertiaForm>
  )
}
